import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { langFlag, sortedLanguages } from '../lib/languageFlags.js'
import { startCaptureJob, type MixSettings } from '../lib/api.js'
import { useCapture } from '../hooks/useCapture.js'

interface NewDubDrawerProps {
  open: boolean
  onClose: () => void
}

function extractVideoId(url: string): string | null {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([^&?/\s]{11})/,
  )
  return m?.[1] ?? null
}

export default function NewDubDrawer({ open, onClose }: NewDubDrawerProps) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { startCapture, captureQueue, sharedStream, setSharedStream } = useCapture()
  const langs = useMemo(() => sortedLanguages(t), [t])
  const [url, setUrl] = useState('')
  const [sourceLang, setSourceLang] = useState('en')
  const [targetLang, setTargetLang] = useState('ko')
  const [originalVol, setOriginalVol] = useState(15)
  const [dubbedVol, setDubbedVol] = useState(100)
  const [urlError, setUrlError] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [queuing, setQueuing] = useState(false)

  const isMobile = !('getDisplayMedia' in (navigator.mediaDevices ?? {}))

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Reset error when URL changes
  useEffect(() => { setUrlError(''); setErrorMsg('') }, [url])

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  const swapLangs = () => {
    if (sourceLang === targetLang) return
    const tmp = sourceLang
    setSourceLang(targetLang)
    setTargetLang(tmp)
  }

  /** Check if the shared stream is still alive. */
  const isStreamLive = () => {
    if (!sharedStream) return false
    return sharedStream.getAudioTracks().some(t => t.readyState === 'live')
  }

  const handleQueue = async () => {
    setUrlError('')
    setErrorMsg('')

    if (!url.trim()) {
      setUrlError(t('capture.invalidUrl'))
      return
    }
    const videoId = extractVideoId(url)
    if (!videoId) {
      setUrlError(t('capture.invalidUrl'))
      return
    }

    if (isMobile) {
      onClose()
      return
    }

    // Reuse existing stream if alive, otherwise request a new one.
    // getDisplayMedia MUST be the first async call in the click handler (user gesture).
    let audioStream: MediaStream
    if (isStreamLive()) {
      audioStream = sharedStream!
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          // @ts-expect-error — Chrome-only: mute local playback while capturing
          audio: { suppressLocalAudioPlayback: true },
          preferCurrentTab: true,
          video: true,
        })
        stream.getVideoTracks().forEach(track => track.stop())

        const audioTracks = stream.getAudioTracks()
        if (audioTracks.length === 0) {
          stream.getTracks().forEach(t => t.stop())
          setUrlError(t('capture.noAudioTracks'))
          return
        }
        audioStream = new MediaStream(audioTracks)
        setSharedStream(audioStream)
      } catch (err) {
        const name = (err as Error).name
        if (name === 'NotAllowedError' || name === 'AbortError') {
          setUrlError(t('capture.errorDenied'))
        } else {
          setErrorMsg((err as Error).message)
        }
        return
      }
    }

    setQueuing(true)

    // Fetch video title (best-effort)
    let videoTitle = ''
    const videoThumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    try {
      const oembed = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      if (oembed.ok) {
        const data = await oembed.json() as { title?: string }
        videoTitle = data.title ?? ''
      }
    } catch { /* non-critical */ }

    const mixSettings: MixSettings = { duckPercent: 100 - originalVol, originalVolume: originalVol, dubbedVolume: dubbedVol }

    try {
      const job = await startCaptureJob({
        targetLang,
        captureRate: 2,
        videoUrl: url,
        videoTitle,
        videoThumbnailUrl,
        sourceLang,
        mixSettings,
      })

      startCapture({
        jobId:      job.id,
        videoId,
        videoUrl:   url,
        targetLang,
        stream:     audioStream,
        uploadUrl:  job.uploadUrl,
        mixSettings,
      })

      void qc.invalidateQueries({ queryKey: ['jobs'] })
      onClose()

      // Reset form for next use
      setUrl('')
      setUrlError('')
      setErrorMsg('')
    } catch (err) {
      // Only stop stream if nothing else in the queue needs it
      if (captureQueue.length === 0) {
        audioStream.getTracks().forEach(t => t.stop())
        setSharedStream(null as unknown as MediaStream)
      }
      setErrorMsg((err as Error).message)
    }

    setQueuing(false)
  }

  const queuePosition = captureQueue.length

  return (
    <div className={`drawer-overlay${open ? ' open' : ''}`} onClick={handleOverlayClick}>
      <div className="drawer-panel">
        <div className="drawer-header">
          <span className="drawer-title">{t('shell.newDub')}</span>
          <button className="drawer-close" onClick={onClose}>
            <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className="drawer-body">
          {/* URL */}
          <div className="drawer-field">
            <div className="field-label">{t('capture.urlLabel')}</div>
            <div className={`drawer-input${urlError ? ' error' : ''}`}>
              <svg viewBox="0 0 24 24"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" /></svg>
              <input
                type="text"
                placeholder={t('capture.urlPlaceholder')}
                value={url}
                onChange={e => setUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void handleQueue() }}
              />
            </div>
            {urlError && <div style={{ color: 'var(--color-error, #ef4444)', fontSize: 12, marginTop: 4 }}>{urlError}</div>}
            {errorMsg && <div style={{ color: 'var(--color-error, #ef4444)', fontSize: 12, marginTop: 4 }}>{errorMsg}</div>}
          </div>

          {/* Languages */}
          <div className="drawer-field">
            <div className="field-label">{t('shell.languages')}</div>
            <div className="lang-row">
              <select className="drawer-select" value={sourceLang} onChange={e => setSourceLang(e.target.value)}>
                {langs.map(l => (
                  <option key={l} value={l}>{langFlag(l)} {t(`languages.${l}`)}</option>
                ))}
              </select>
              <button className="lang-swap" onClick={swapLangs}>
                <svg viewBox="0 0 24 24"><path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" /></svg>
              </button>
              <select className="drawer-select" value={targetLang} onChange={e => setTargetLang(e.target.value)}>
                {langs.map(l => (
                  <option key={l} value={l}>{langFlag(l)} {t(`languages.${l}`)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Audio Mix */}
          <div className="drawer-field">
            <div className="field-label">{t('shell.audioMix')}</div>
            <div className="vol-grid">
              <div className="vol-item">
                <label>{t('shell.originalDucked')}</label>
                <div className="vol-track-wrap">
                  <svg viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 010 7.07" /></svg>
                  <input type="range" className="vol-slider" min="0" max="100" value={originalVol} style={{ '--val': `${originalVol}%` } as React.CSSProperties} onChange={e => setOriginalVol(Number(e.target.value))} />
                  <span className="vol-drawer-val">{originalVol}%</span>
                </div>
              </div>
              <div className="vol-item">
                <label>{t('shell.dubbedVoice')}</label>
                <div className="vol-track-wrap">
                  <svg viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 010 7.07" /><path d="M19.07 4.93a10 10 0 010 14.14" /></svg>
                  <input type="range" className="vol-slider" min="0" max="100" value={dubbedVol} style={{ '--val': `${dubbedVol}%` } as React.CSSProperties} onChange={e => setDubbedVol(Number(e.target.value))} />
                  <span className="vol-drawer-val">{dubbedVol}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pipeline preview */}
          <div className="drawer-field">
            <div className="field-label">{t('shell.pipeline')}</div>
            <div className="pipeline-preview">
              <div className="pipeline-title">{t('shell.steps')}</div>
              <div className="pipeline-step"><div className="pipeline-num">1</div><span className="pipeline-label">{t('shell.step1')}</span><span className="pipeline-time">—</span></div>
              <div className="pipeline-div" />
              <div className="pipeline-step"><div className="pipeline-num">2</div><span className="pipeline-label">{t('shell.step2')}</span><span className="pipeline-time">~1 min</span></div>
              <div className="pipeline-step"><div className="pipeline-num">3</div><span className="pipeline-label">{t('shell.step3')}</span><span className="pipeline-time">~2 min</span></div>
              <div className="pipeline-step"><div className="pipeline-num">4</div><span className="pipeline-label">{t('shell.step4')}</span><span className="pipeline-time">~1 min</span></div>
              <div className="pipeline-step"><div className="pipeline-num">5</div><span className="pipeline-label">{t('shell.step5')}</span><span className="pipeline-time">~1 min</span></div>
            </div>
          </div>
        </div>

        <div className="drawer-footer">
          <span className="drawer-note">{t('shell.drawerNote')}</span>
          {queuePosition > 0 && (
            <span style={{ fontSize: 11, color: 'var(--muted-app)' }}>
              {t('shell.queuePosition', { position: queuePosition + 1 })}
            </span>
          )}
          <button className="btn-queue" onClick={() => void handleQueue()} disabled={queuing}>
            <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            {queuing ? t('shell.queuing', 'Starting...') : t('shell.queueDub')}
          </button>
        </div>
      </div>
    </div>
  )
}
